import {
  Box,
  Drawer,
  FormControlLabel,
  FormLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  ActionButton,
  DrawerSearchableSelectField,
  FieldInput,
  IconCheckCircleOutlined,
  IconClose,
  IconSearchOutlined,
  validateAssignCarrierDotRequired,
  validateCompanyNameText,
  validateEmailAddress,
  validateLettersSpacesCommasNameRequired,
  validateOptionalTextMaxLen,
  sanitizeLettersSpacesCommasMax,
  type CreateBookingOption,
  type InviteCarrierLookupResult,
} from "design-components";
import type { ChangeEvent, FormEvent } from "react";
import { useCarrierInviteDrawer } from "../hooks/use-carrier-invite-drawer.hook";
import type { CarrierInviteSubmitPayload } from "../types";

const INVITE_DESCRIPTION =
  "An invitation email will be sent to the carrier with onboarding instructions.";

const EMAIL_ONBOARDING_HELPER =
  "It's recommended that you use this initial email for onboarding to prevent fraud.";

export interface InviteCarrierDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CarrierInviteSubmitPayload) => void | Promise<void>;
  onDotLookup?: (dotNumber: string) => Promise<InviteCarrierLookupResult>;
  customerOptions: CreateBookingOption[];
  customersLoading?: boolean;
  customersLoadError?: string | null;
  submitting?: boolean;
}

function LookupStatusTag(props: { label: string; value: string }) {
  const { label, value } = props;
  return (
    <Box className="assign-carrier-drawer__status-tag">
      <Typography
        component="span"
        className="assign-carrier-drawer__status-tag-label"
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box className="assign-carrier-drawer__status-tag-icon" aria-hidden>
          <IconCheckCircleOutlined size={18} />
        </Box>
        <Typography
          component="span"
          className="assign-carrier-drawer__status-tag-value"
        >
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}

export function InviteCarrierDrawer(props: InviteCarrierDrawerProps) {
  const {
    open,
    onClose,
    onSubmit,
    onDotLookup,
    customerOptions,
    customersLoading = false,
    customersLoadError = null,
    submitting = false,
  } = props;

  const {
    customerValue,
    handleCustomerChange,
    handleCustomerBlur,
    hasFederalDot,
    handleFederalDotToggle,
    showCarrierDetailsFields,
    dotNumber,
    handleDotNumberChange,
    dotLookupLoading,
    lookupDetails,
    runDotLookup,
    carrierName,
    setCarrierName,
    contactName,
    setContactName,
    email,
    setEmail,
    phone,
    handlePhoneChange,
    scac,
    setScac,
    stateProvincialId,
    setStateProvincialId,
    businessAddress,
    setBusinessAddress,
    fieldErrors,
    setFieldError,
    validateFieldOnBlur,
    validate,
    isFormValid,
    buildPayload,
  } = useCarrierInviteDrawer({ open, onDotLookup });

  const handleSubmitForm = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    await onSubmit(buildPayload());
  };

  const handleCancel = () => {
    if (submitting) return;
    onClose();
  };

  const handleDotSearch = async () => {
    const dotErr = validateAssignCarrierDotRequired(dotNumber, true);
    if (dotErr) {
      setFieldError("dotNumber", dotErr);
      return;
    }
    await runDotLookup(dotNumber);
  };

  const canSearchDot = Boolean(dotNumber.trim()) && !dotLookupLoading && !submitting;
  const dotInputError = fieldErrors.dotNumber ?? null;
  let dotStatusMessage: string | null = null;
  if (dotInputError) {
    dotStatusMessage = dotInputError;
  } else if (dotLookupLoading) {
    dotStatusMessage = "Looking up carrier…";
  }

  const statusTags =
    hasFederalDot && lookupDetails != null ? (
      <Stack
        direction="row"
        useFlexGap
        gap={2.5}
        flexWrap="wrap"
        className="assign-carrier-drawer__status-tags"
      >
        <LookupStatusTag label="DOT status" value={lookupDetails.dotStatus} />
        <LookupStatusTag label="Risk score" value={lookupDetails.riskScore} />
        <LookupStatusTag
          label="Safety score"
          value={lookupDetails.safetyScore}
        />
      </Stack>
    ) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting ? undefined : onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgb(255 255 255 / 50%)",
          },
        },
        paper: {
          className: "user-form-drawer__paper",
        },
      }}
    >
      <Box
        className="user-form-drawer assign-carrier-drawer"
        component="form"
        onSubmit={handleSubmitForm}
        noValidate
      >
        <Stack spacing={2.5} className="user-form-drawer__body">
          <Stack spacing={1.25} className="user-form-drawer__header-block">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography className="user-form-drawer__title" component="h2">
                Invite carrier
              </Typography>
              <IconButton
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                aria-label="Close"
                className="user-form-drawer__close"
              >
                <IconClose aria-hidden size={24} />
              </IconButton>
            </Stack>
            <Typography
              className="user-form-drawer__description"
              variant="body2"
            >
              {INVITE_DESCRIPTION}
            </Typography>
          </Stack>

          <Box className="user-form-drawer__fields-scroll">
            <Stack
              spacing={0}
              className="user-form-drawer__fields assign-carrier-drawer__invite-flow"
            >
              <DrawerSearchableSelectField
                open={open}
                label="Customer"
                required
                placeholder="Search customer"
                options={customerOptions}
                value={customerValue}
                onValueChange={handleCustomerChange}
                onBlur={handleCustomerBlur}
                loading={customersLoading}
                loadError={customersLoadError}
                emptyListMessage="No customers are available."
                noMatchMessage="No customers match your search."
                loadingMessage="Loading customers…"
                errorText={fieldErrors.customerId}
                suggestionsListAriaLabel="Customer search results"
                fieldDisabled={submitting}
              />

              <FormControlLabel
                className="toggle-label assign-carrier-drawer__federal-dot-toggle"
                control={
                  <Switch
                    className="toggle"
                    checked={hasFederalDot}
                    disabled={submitting}
                    onChange={(_, checked) => handleFederalDotToggle(checked)}
                    inputProps={{ "aria-label": "Carrier has a federal DOT" }}
                  />
                }
                label="Carrier has a federal DOT"
              />

              {hasFederalDot ? (
                <Stack
                  spacing={1.875}
                  className="assign-carrier-drawer__dot-block"
                >
                  <Typography
                    variant="body2"
                    className="assign-carrier-drawer__dot-block-helper"
                    component="p"
                  >
                    Enter the carrier&apos;s DOT number to search for them in
                    Carrier OK.
                  </Typography>
                  <Box className="assign-carrier-drawer__dot-field">
                    <Stack direction="row" spacing={0} className="input-label-row">
                      <FormLabel className="input-label" component="label">
                        DOT
                      </FormLabel>
                      <Typography component="span" className="input-required">
                        *
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      useFlexGap
                      gap={1.875}
                      className="assign-carrier-drawer__dot-row"
                    >
                      <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                        <FieldInput
                          fullWidth
                          disabled={submitting}
                          error={Boolean(dotInputError)}
                          outlinedInputProps={{
                            value: dotNumber,
                            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                              handleDotNumberChange(e.target.value);
                            },
                            onKeyDown: (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (canSearchDot) {
                                  void handleDotSearch();
                                }
                              }
                            },
                            inputProps: { autoComplete: "off" },
                          }}
                        />
                      </Box>
                      <IconButton
                        type="button"
                        color="primary"
                        aria-label="Search by DOT number"
                        className="assign-carrier-drawer__dot-search-btn"
                        disabled={!canSearchDot}
                        onClick={() => void handleDotSearch()}
                      >
                        <IconSearchOutlined aria-hidden size={24} />
                      </IconButton>
                    </Stack>
                    <Typography
                      variant="caption"
                      component="p"
                      aria-live="polite"
                      className={`assign-carrier-drawer__dot-helper assign-carrier-drawer__dot-helper-slot${
                        dotInputError ? " assign-carrier-drawer__dot-helper--error" : ""
                      }`}
                    >
                      {dotStatusMessage ?? "\u00A0"}
                    </Typography>
                  </Box>
                </Stack>
              ) : null}

              {showCarrierDetailsFields ? (
                <>
                  <FieldInput
                    label="Carrier name"
                    required
                    fullWidth
                    disabled={submitting}
                    error={Boolean(fieldErrors.carrierName)}
                    helperText={fieldErrors.carrierName}
                    outlinedInputProps={{
                      value: carrierName,
                      inputProps: { maxLength: 50 },
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        const v = e.target.value;
                        setCarrierName(v);
                        if (fieldErrors.carrierName) {
                          setFieldError(
                            "carrierName",
                            validateCompanyNameText(v),
                          );
                        }
                      },
                      onBlur: () => validateFieldOnBlur("carrierName"),
                    }}
                  />

                  <FieldInput
                    label="Contact name"
                    required
                    fullWidth
                    disabled={submitting}
                    error={Boolean(fieldErrors.contactName)}
                    helperText={fieldErrors.contactName}
                    outlinedInputProps={{
                      value: contactName,
                      inputProps: { maxLength: 50 },
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        const v = sanitizeLettersSpacesCommasMax(
                          e.target.value,
                          50,
                        );
                        setContactName(v);
                        if (fieldErrors.contactName) {
                          setFieldError(
                            "contactName",
                            validateLettersSpacesCommasNameRequired(v),
                          );
                        }
                      },
                      onBlur: () => validateFieldOnBlur("contactName"),
                    }}
                  />

                  <FieldInput
                    label="Email"
                    required
                    fullWidth
                    disabled={submitting}
                    error={Boolean(fieldErrors.email)}
                    helperText={
                      fieldErrors.email ??
                      (hasFederalDot ? EMAIL_ONBOARDING_HELPER : undefined)
                    }
                    outlinedInputProps={{
                      type: "email",
                      value: email,
                      inputProps: { maxLength: 50 },
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        const v = e.target.value;
                        setEmail(v);
                        if (fieldErrors.email) {
                          setFieldError(
                            "email",
                            validateEmailAddress(v, {
                              required: true,
                              minLen: 3,
                              maxLen: 50,
                            }),
                          );
                        }
                      },
                      onBlur: () => validateFieldOnBlur("email"),
                    }}
                  />

                  <FieldInput
                    label="Phone"
                    fullWidth
                    disabled={submitting}
                    outlinedInputProps={{
                      value: phone,
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        handlePhoneChange(e.target.value);
                      },
                      inputProps: {
                        autoComplete: "tel",
                        inputMode: "tel",
                      },
                    }}
                  />

                  <FieldInput
                    label="SCAC"
                    fullWidth
                    disabled={submitting}
                    error={Boolean(fieldErrors.scac)}
                    helperText={fieldErrors.scac}
                    outlinedInputProps={{
                      value: scac,
                      inputProps: { maxLength: 20 },
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        setScac(e.target.value.slice(0, 20));
                      },
                      onBlur: () => {
                        const msg = validateOptionalTextMaxLen(scac, 20);
                        setFieldError("scac", msg);
                      },
                    }}
                  />

                  {!hasFederalDot ? (
                    <FieldInput
                      label="State/Provincial ID"
                      fullWidth
                      disabled={submitting}
                      error={Boolean(fieldErrors.stateProvincialId)}
                      helperText={fieldErrors.stateProvincialId}
                      outlinedInputProps={{
                        value: stateProvincialId,
                        inputProps: { maxLength: 50 },
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                          setStateProvincialId(e.target.value.slice(0, 50));
                        },
                      }}
                    />
                  ) : null}

                  <FieldInput
                    label="Business address"
                    fullWidth
                    disabled={submitting}
                    error={Boolean(fieldErrors.businessAddress)}
                    helperText={fieldErrors.businessAddress}
                    outlinedInputProps={{
                      value: businessAddress,
                      inputProps: { maxLength: 100 },
                      onChange: (e: ChangeEvent<HTMLInputElement>) => {
                        setBusinessAddress(e.target.value.slice(0, 100));
                      },
                    }}
                  />

                  {statusTags}
                </>
              ) : null}
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={1.25}
            className="user-form-drawer__actions"
          >
            <ActionButton
              type="submit"
              actionVariant="contained-primary"
              disabled={submitting || !isFormValid}
              size="small"
            >
              Invite carrier
            </ActionButton>
            <ActionButton
              type="button"
              actionVariant="neutral"
              onClick={handleCancel}
              disabled={submitting}
              size="small"
            >
              Cancel
            </ActionButton>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
